import React from 'react';
import {
  Box,
  CircularProgress,
  Typography,
  Chip,
  Grid,
} from '@mui/material';
import { User } from '@marageti/z4-sdk/lib/auth';
import { formatDateSafe } from '../audit-utils';

interface UserDataCardProps {
  user: User | null | undefined;
  isLoading: boolean;
  hasError?: boolean;
}

const UserDataCard: React.FC<UserDataCardProps> = ({
  user,
  isLoading,
  hasError = false,
}) => {
  // Not yet fetched -- render nothing (button is in the User column)
  if (user === undefined && !isLoading) {
    return null;
  }

  // Loading
  if (isLoading) {
    return (
      <Box sx={{
        mt: 2, display: 'flex', alignItems: 'center', gap: 1,
      }}
      >
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">
          Loading user information...
        </Typography>
      </Box>
    );
  }

  // Error or not found
  if (user === null) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {hasError
            ? 'Failed to load user information.'
            : 'User information not available.'}
        </Typography>
      </Box>
    );
  }

  // Guard for TypeScript narrowing
  if (!user) {
    return null;
  }

  // User data loaded
  return (
    <Grid
      container
      spacing={2}
      sx={{
        borderTop: '1px solid', borderColor: 'common.grey', mt: 1.5,
      }}
    >
      <Grid item xs={12} sm={4} md={3} lg={2}>
        <Typography variant="captionBold" color="text.secondary" component="div">
          User Email
        </Typography>
        <Typography variant="caption" component="div">
          {user.email}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={4} md={3} lg={2}>
        <Typography variant="captionBold" color="text.secondary" component="div">
          User Status
        </Typography>
        <Typography variant="caption" component="div">
          <Chip label={user.status} size="small" variant="outlined" />
        </Typography>
      </Grid>
      <Grid item xs={12} sm={4} md={3} lg={2}>
        <Typography variant="captionBold" color="text.secondary" component="div">
          User Roles
        </Typography>
        <Typography variant="caption" component="div">
          {user.roles?.join(', ') || '-'}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={4} md={3} lg={2}>
        <Typography variant="captionBold" color="text.secondary" component="div">
          User Created At
        </Typography>
        <Typography variant="caption" component="div">
          {formatDateSafe(user.createdAt)}
        </Typography>
      </Grid>
      <Grid item xs={12} sm={4} md={3} lg={2}>
        <Typography variant="captionBold" color="text.secondary" component="div">
          User Updated At
        </Typography>
        <Typography variant="caption" component="div">
          {formatDateSafe(user.updatedAt)}
        </Typography>
      </Grid>
    </Grid>
  );
};

export default UserDataCard;
